import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  PhoneCall,
  Edit2,
  Trash2,
  User,
  Phone,
  Building,
  Clock,
  Shield,
  Edit,
  Save,
  X,
  UserCircle,
} from "lucide-react";
import { useNavigate, useNavigation, useParams } from "react-router-dom";
import axios from "axios";
import { Button, CircularProgress, Divider, InputBase, MenuItem, Select, Tooltip } from "@mui/material";
import DeleteContactModal from "../modals/DeleteContactModal";
import { formatPhoneNumber } from "../utils/formatnumber";
import { useOrgUsers, getAssignedUserLabel } from "../../hooks/useOrgUsers";

export default function ContactDetailPage() {
  const { phoneNumber } = useParams();
  const [errorMsg, setErrorMsg] = useState(null);
  const [contact, setContact] = useState(null);
  const [edit, setEdit] = useState(false);
  const [contact_editable, setContact_editable] = useState(null);



  const getCallIconColor = (type) => {
    switch (type) {
      case "incoming":
        return "text-green-500";
      case "outgoing":
        return "text-blue-500";
      case "missed":
        return "text-red-500";
      default:
        return "text-gray-500";
    }
  };


  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  const [call_history, setCallHistory] = useState(null);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(()=>{
  setTotalPages( Math.ceil((call_history?.length || 0) / ITEMS_PER_PAGE))
  },[call_history])
  const [loader3, setLoader3] = useState(false);
  const getCallHistory = async () => {
    setLoader3(true)
    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "get-callHistory",
        {
          phoneNumber,
          organizationId: JSON.parse(localStorage.getItem("organization")).orgId
            .S,
        }
      );
      //setCallHistory(response.data.calls);
      function formatDuration(seconds) {
        if (!seconds || seconds <= 0) return "0m 0s";
      
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
      
        return `${mins}m ${secs}s`;
      }
      
      function formatDate(timestamp) {
        return new Date(timestamp * 1000).toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }
      
      function mapCallType(call, phoneNumber) {
        if (!call.fromNumber || !call.toNumber) return "missed";
      
        if (call.fromNumber === phoneNumber) return "outgoing";
        if (call.toNumber === phoneNumber) return "incoming";
      
        return "missed";
      }
      
      // -----------------------------------------------------
      // SET CALL HISTORY
      // -----------------------------------------------------
      setCallHistory(
        response.data.calls.map((call) => ({
          type: mapCallType(call, phoneNumber),
          date: call.startTime ? formatDate(call.startTime) : "Unknown date",
          duration: formatDuration(call.duration),
        }))
      );
      setLoader3(false)
    } catch (err) {
      // Log for production diagnostics when get call history fails
      console.error("ContactDetailPage: get call history failed", err.response?.data || err.message || err);
      setErrorMsg(err.response?.data?.message || "Access denied");
      setLoader3(false)
    }
  };





  const getContactDetails = async () => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "get-contact",
        {
          phoneNumber,
          organizationId: JSON.parse(localStorage.getItem("organization")).orgId
            .S,
        }
      );
      setContact(response.data.contact);
      setContact_editable(response.data.contact);
    } catch (err) {
      // Log for production diagnostics when get contact details fails
      console.error("ContactDetailPage: get contact details failed", err.response?.data || err.message || err);
      setErrorMsg(err.response?.data?.message || "Access denied");
    }
  };

  useEffect(() => {
    getContactDetails();
    getCallHistory()
  }, []);

  const navigate = useNavigate();

  const [loader2, setLoader2] = useState(false);

  const organizationId = JSON.parse(localStorage.getItem("organization"))?.orgId?.S;
  const { users: orgUsers, userNameById } = useOrgUsers(organizationId);

  const updateContact = async () => {
    setLoader2(true);
    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "update-contact",
        {
          phoneNumber: contact.phoneNumber,
          organizationId: contact_editable.organizationId,
          contact_name: contact_editable.name,
          company: contact_editable.company,
          newPhoneNumber: contact_editable.phoneNumber,
          assignedUserId: contact_editable.assignedUserId || null,
        }
      );
      setContact(response.data.contact);
      setContact_editable(response.data.contact);

      navigate("/dashboard/contact/" + response.data.contact.phoneNumber);

      setEdit(false);
      setLoader2(false);
    } catch (err) {
      // Log for production diagnostics when update contact fails
      console.error("ContactDetailPage: update contact failed", err.response?.data || err.message || err);
      setErrorMsg(err.response?.data?.message || "Access denied");
      setLoader2(false);
    }
  };

  const [openDeleteContactModal, setOpenDeleteContactModal] = useState(false);
  const handleDelete = async (contact) => {
    setLoader2(true);
    try {
      let response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "delete-contact",
        {
          phoneNumber: contact.phoneNumber,
          organizationId: contact.organizationId,
        }
      );

      navigate("/dashboard/contacts");
    } catch (err) {
      // Log for production diagnostics when delete contact fails
      console.error("ContactDetailPage: delete contact failed", err.response?.data || err.message || err);
      setErrorMsg(err.response?.data?.message || "Access denied");
    }
  };

  // -------------------------------
  // If error, show error page only
  // -------------------------------
  if (errorMsg) {
    return (
      <div className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-4">
            <ArrowLeft
              className="w-6 h-6 text-gray-500 cursor-pointer"
              onClick={() => {
                navigate("/dashboard/contacts");
              }}
            />
            <h1 className="text-2xl font-semibold text-gray-800">
              Contact Details
            </h1>
          </div>
        </div>

        <div className="flex justify-center items-center h-48 bg-white rounded-lg p-6">
          <p className="text-gray-600">
            OOps contact not found in this organization contact{" "}
            <a
              href="mailto:hello@twilanswer.com"
              className=" text-purple-700 underline"
            >
              help
            </a>{" "}
          </p>
        </div>
      </div>
    );
  }

  // -------------------------------
  // While loading contact
  // -------------------------------
  if (!contact) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  // -------------------------------
  // Contact details page
  // -------------------------------

  return (
    <div className="p-4 md:p-6">
      <DeleteContactModal
        open={openDeleteContactModal}
        onClose={() => setOpenDeleteContactModal(false)}
        onDelete={() => handleDelete(contact)}
        contact={contact}
        loader={loader2}
      />
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-4">
          <ArrowLeft
            className="w-6 h-6 text-gray-500 cursor-pointer"
            onClick={() => {
              navigate("/dashboard/contacts");
             

            }}
          />
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">
              Contact Details
            </h1>
            <div className="text-gray-600 text-[12px]">{contact.name}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
          onClick={()=>{
            navigate("/dashboard/dialer?recipient_number="+contact.phoneNumber);
          }}
           className="flex items-center gap-2 bg-[#16803C] text-white font-semibold text-[14px] px-4 py-2 rounded-md hover:bg-green-600">
            <PhoneCall className="w-4 h-4" /> Call Contact
          </button>
          {!edit ? (
            <Tooltip
  title="Synced contacts are not editable in Twilanswer"
  arrow
  disableHoverListener={contact.source === "manual"}
>
  <span>
    <button
      disabled={contact.source !== "manual"}
      onClick={() => setEdit(true)}
      className={
        contact.source === "manual"
          ? "flex items-center gap-2 h-full border border-gray-300 px-3 text-[14px] font-semibold bg-white py-1 rounded-md hover:bg-gray-100"
          : "flex items-center gap-2 h-full border border-gray-300 text-gray-400 text-[14px] font-semibold px-4 py-2 rounded-md cursor-not-allowed bg-gray-100"
      }
    >
      {loader2 ? (
        <CircularProgress size={16} />
      ) : (
        <>
          <Edit className="w-4 h-4" /> Edit
        </>
      )}
    </button>
  </span>
</Tooltip>
          ) : (
            <button
              onClick={() => updateContact()}
              className="flex items-center gap-2 border border-gray-300 px-3 text-[14px] text-white font-semibold bg-[#4338CA] py-1 rounded-md "
            >
              {loader2 ? (
                <CircularProgress
                  size={"20px"}
                  sx={{ fontSize: "14px", color: "white" }}
                />
              ) : (
                <>
                  <Save className="w-4 h-4" /> Save
                </>
              )}
            </button>
          )}

          {!edit ? (
            <Tooltip
  title="Synced contacts are not editable in Twilanswer"
  arrow
  disableHoverListener={contact.source === "manual"}
>
            <button
              onClick={() => setOpenDeleteContactModal(true)}
              disabled={contact.source != 'manual' && true}
              className={
                contact.source == 'manual' ? 
                "flex items-center gap-2 border border-red-500 text-[#B91C1B] text-[14px] font-semibold px-4 py-2 rounded-md hover:bg-red-50"
                : "flex items-center gap-2 border border-gray-300 text-gray-400 text-[14px] font-semibold px-4 py-2 rounded-md cursor-not-allowed bg-gray-100"
              }
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
            </Tooltip>
          ) : (
            <Button
              onClick={() => {
                setContact_editable(contact);
                setEdit(false);
              }}
              sx={{
                textTransform: "none",
                bgcolor: "white",
                color: "black",
                border: "1px solid #D1D5DB",
                px: 2,
                py: 1,
                borderRadius: "8px",
              }}
            >
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          )}

        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Left: Contact Info */}
        <div className="w-full h-fit md:w-1/3 bg-white rounded-lg  p-6 flex flex-col items-center gap-4 border-[#E6E7EB] border-[1.6px] ">
          <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center">
            <User className="w-10 h-10 text-purple-500" />
          </div>
          <h2 className="text-xl font-semibold text-center">
            {edit ? contact_editable.name : contact.name}
          </h2>

          <div className="w-full flex flex-col gap-2 mt-4">
            <div className="flex items-center gap-2 text-gray-600">
              <User className="w-4 h-4" />
              <div>
                {!edit ? (
                  <div className="font-bold text-black text-[14px]">
                    {contact.name}
                  </div>
                ) : (
                  <InputBase
                    value={contact_editable.name}
                    onChange={(e) => {
                      setContact_editable((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }));
                    }}
                    sx={{
                      border: "1px solid #D1D5DB",
                      borderRadius: "7px",
                      fontSize: "13px",
                      pl: 1,
                    }}
                  />
                )}
                <div className="text-[12px]">Name</div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Phone className="w-4 h-4" />
              <div>
                {!edit ? (
                  <div className="font-bold text-black text-[14px]">
                    {formatPhoneNumber(contact.phoneNumber)}
                  </div>
                ) : (
                  <InputBase
                    value={contact_editable.phoneNumber}
                    onChange={(e) => {
                      setContact_editable((prev) => ({
                        ...prev,
                        phoneNumber: e.target.value,
                      }));
                    }}
                    sx={{
                      border: "1px solid #D1D5DB",
                      borderRadius: "7px",
                      fontSize: "13px",
                      pl: 1,
                    }}
                  />
                )}

                <div className="text-[12px]">Phone</div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Building className="w-4 h-4" />
              <div>
                {!edit ? (
                  <div className="font-bold text-black text-[14px]">
                    {contact.company}
                  </div>
                ) : (
                  <InputBase
                    value={contact_editable.company}
                    onChange={(e) => {
                      setContact_editable((prev) => ({
                        ...prev,
                        company: e.target.value,
                      }));
                    }}
                    sx={{
                      border: "1px solid #D1D5DB",
                      borderRadius: "7px",
                      fontSize: "13px",
                      pl: 1,
                    }}
                  />
                )}

                <div className="text-[12px]">Company</div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <UserCircle className="w-4 h-4" />
              <div className="w-full">
                {!edit ? (
                  <div className="font-bold text-black text-[14px]">
                    {getAssignedUserLabel(contact.assignedUserId, userNameById)}
                  </div>
                ) : (
                  <Select
                    displayEmpty
                    size="small"
                    fullWidth
                    value={contact_editable.assignedUserId || ""}
                    onChange={(e) => {
                      setContact_editable((prev) => ({
                        ...prev,
                        assignedUserId: e.target.value,
                      }));
                    }}
                    sx={{
                      border: "1px solid #D1D5DB",
                      borderRadius: "7px",
                      fontSize: "13px",
                      fontWeight: 600,
                      "& .MuiSelect-select": { py: 0.5, pl: 1 },
                    }}
                  >
                    <MenuItem value="">
                      <em>Unassigned</em>
                    </MenuItem>
                    {orgUsers.map((orgUser) => (
                      <MenuItem key={orgUser.user_id} value={orgUser.user_id}>
                        {orgUser.name || orgUser.email}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                <div className="text-[12px]">Assigned to</div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-4 h-4" />
              <div>
                <div className="font-bold text-black text-[14px]">
                  {new Date(contact.createdAt * 1000).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </div>
                <div className="text-[12px]">Added</div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-gray-600 flex-wrap">
              <Shield className="w-4 h-4" />
              <span className="bg-gray-100 text-black font-semibold text-[14px] px-2 py-0.5 rounded-full text-xs">
                {contact.source ? contact.source.charAt(0).toUpperCase() + contact.source.slice(1) : "Unknown"}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Call History */}
        <div className="w-full md:w-2/3 bg-white rounded-lg h-fit   border-[#E6E7EB] border-[1.6px]">
          <h3 className="text-lg font-medium text-gray-800 mb-4 pt-6 px-6">
            Call History
          </h3>
          <p className="text-sm text-gray-500 mb-4 px-6">
            Recent calls with this contact
          </p>

          <Divider />

          <div className="flex flex-col   pb-6">
            {call_history?.slice(
  (currentPage - 1) * ITEMS_PER_PAGE,
  currentPage * ITEMS_PER_PAGE
)?.map((call, index) => (
              <div
                key={index}
                className={`flex flex-col sm:flex-row sm:items-center justify-between px-6 py-3 gap-2 ${
                  index !== call_history?.slice(
  (currentPage - 1) * ITEMS_PER_PAGE,
  currentPage * ITEMS_PER_PAGE
)?.length - 1
                    ? "border-b border-gray-200"
                    : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <PhoneCall
                    className={`w-5 h-5 ${getCallIconColor(call.type)}`}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-800">
                      {call.type.charAt(0).toUpperCase() + call.type.slice(1)}{" "}
                      Call
                    </span>
                    <span className="text-sm text-gray-500">{call.date}</span>
                  </div>
                </div>
                <span className="text-sm text-gray-500">{call.duration}</span>
              </div>
            ))}

            {totalPages > 1 && (
  <div className="flex justify-end items-center gap-2 px-6 pb-4">
    <button
      disabled={currentPage === 1}
      onClick={() => setCurrentPage((p) => p - 1)}
      className="px-3 py-1 border rounded disabled:opacity-50"
    >
      Previous
    </button>

    <span className="text-sm text-gray-600">
      Page {currentPage} of {totalPages}
    </span>

    <button
      disabled={currentPage === totalPages}
      onClick={() => setCurrentPage((p) => p + 1)}
      className="px-3 py-1 border rounded disabled:opacity-50"
    >
      Next
    </button>
  </div>
)}

            {
                call_history?.length < 1 &&(
                    <>
                        <div className="text-center mt-2 text-gray-400">No Call history available</div>
                    </>
                )
            }
          </div>
        </div>
      </div>
    </div>
  );
}
